/**
 * Super Agent API Service
 *
 * Provides API methods for interacting with the Super Agent system
 */

import axiosInstance from './axiosConfig';
import {
  ProcessRequestPayload,
  SuperAgentAsyncResponse,
  SuperAgentStatusResponse,
  ProcessRequestResponse,
  GetExecutionDetailsResponse,
  GetAvailableAgentsResponse,
  GetUserExecutionsResponse,
  GetStatisticsResponse
} from '../../types/superAgent';

class SuperAgentService {
  /**
   * Process a user request through the Super Agent system (Async)
   * Returns immediately with execution_id (202 Accepted)
   *
   * @param payload - Request payload containing the user's request and optional max_agents
   * @returns Promise resolving to async response with execution_id
   */
  async processRequest(payload: ProcessRequestPayload): Promise<SuperAgentAsyncResponse> {
    try {
      const response = await axiosInstance.post<SuperAgentAsyncResponse>(
        '/super-agent/process',
        payload
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: processRequest failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get execution status (for polling)
   * Returns current progress and results when completed
   *
   * @param executionId - The execution ID to check status for
   * @returns Promise resolving to status response with progress info
   */
  async getExecutionStatus(executionId: number): Promise<SuperAgentStatusResponse> {
    try {
      const response = await axiosInstance.get<SuperAgentStatusResponse>(
        `/super-agent/status/${executionId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: getExecutionStatus failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get execution details by ID
   *
   * @param executionId - The execution ID
   * @returns Promise resolving to execution details with results
   */
  async getExecutionDetails(executionId: number): Promise<GetExecutionDetailsResponse> {
    try {
      const response = await axiosInstance.get<GetExecutionDetailsResponse>(
        `/super-agent/executions/${executionId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: getExecutionDetails failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get user's execution history
   *
   * @param limit - Maximum number of executions to retrieve (default: 10)
   * @returns Promise resolving to execution history
   */
  async getUserExecutions(limit: number = 10): Promise<GetUserExecutionsResponse> {
    try {
      const response = await axiosInstance.get<GetUserExecutionsResponse>(
        '/super-agent/executions',
        {
          params: { limit }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: getUserExecutions failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get available specialist agents
   *
   * @returns Promise resolving to list of available agents
   */
  async getAvailableAgents(): Promise<GetAvailableAgentsResponse> {
    try {
      const response = await axiosInstance.get<GetAvailableAgentsResponse>(
        '/super-agent/agents'
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: getAvailableAgents failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get system statistics
   *
   * @returns Promise resolving to system statistics
   */
  async getStatistics(): Promise<GetStatisticsResponse> {
    try {
      const response = await axiosInstance.get<GetStatisticsResponse>(
        '/super-agent/stats'
      );
      return response.data;
    } catch (error: any) {
      console.error('SuperAgentService: getStatistics failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors and transform them into user-friendly messages
   *
   * @param error - The error object from axios
   * @returns Transformed error with user-friendly message
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      return new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('No response from server. Please check your internet connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }
}

// Export singleton instance
const superAgentService = new SuperAgentService();
export default superAgentService;
